import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import DeleteSessionCostModal from "../components/DeleteSessionCostModal";
import type { SessionCostMetric } from "../types";

function Stat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-[10px] bg-surface2 px-3 py-2.5">
      <p className="text-[0.6rem] uppercase tracking-[0.1em] text-text-tertiary font-semibold">{label}</p>
      <p className="text-sm text-ink font-semibold mt-1">{value}</p>
      {hint && <p className="text-[0.68rem] text-text-tertiary mt-0.5">{hint}</p>}
    </div>
  );
}

const formatEur = (value: number) => new Intl.NumberFormat("en-IE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
}).format(value);

const formatDuration = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds));
  return `${Math.floor(total / 60)}m ${total % 60}s`;
};

function ToolCalls({ item }: { item: SessionCostMetric }) {
  const calls = ([
    ["Errors", item.error_tool_calls],
    ["Vocabulary", item.vocabulary_tool_calls],
    ["Knowledge", item.knowledge_tool_calls],
    ["Level", item.level_tool_calls],
  ] as Array<[string, number]>).filter(([, count]) => count > 0);

  if (!calls.length) return <span className="text-text-tertiary">None</span>;
  return <span className="text-text-secondary">{calls.map(([label, count]) => `${label}: ${count}`).join(" · ")}</span>;
}

export default function AdminSessionCostsPage() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [items, setItems] = useState<SessionCostMetric[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [costToDelete, setCostToDelete] = useState<SessionCostMetric | null>(null);

  useEffect(() => {
    api.getMe()
      .then((me) => {
        if (!me.is_admin) {
          navigate("/", { replace: true });
          return;
        }
        setAllowed(true);
      })
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!allowed) return;
    api.getSessionCosts().then(setItems).catch((err) => {
      setError(err instanceof Error ? err.message : "Failed to load session costs.");
    });
  }, [allowed]);

  const onCostDeleted = (id: number) => {
    setItems((current) => current?.filter((cost) => cost.id !== id) ?? null);
    setCostToDelete(null);
  };

  const totals = useMemo(() => {
    const rows = items ?? [];
    return {
      sessions: rows.length,
      sessionSeconds: rows.reduce((sum, item) => sum + (item.duration_s ?? 0), 0),
      inputSeconds: rows.reduce((sum, item) => sum + item.input_audio_seconds, 0),
      outputSeconds: rows.reduce((sum, item) => sum + item.output_audio_seconds, 0),
      modelTurns: rows.reduce((sum, item) => sum + item.model_turns, 0),
      tools: rows.reduce((sum, item) => sum + item.error_tool_calls + item.vocabulary_tool_calls + item.knowledge_tool_calls + item.level_tool_calls, 0),
      estimatedLiveCost: rows.reduce((sum, item) => sum + item.estimated_live_cost_eur, 0),
    };
  }, [items]);

  if (!allowed) return null;

  return (
    <>
      <div className="max-w-5xl mx-auto w-full px-5 sm:px-8 py-10">
        <header className="mb-6 animate-rise">
          <h1>Session costs</h1>
          <p className="text-text-tertiary text-[0.9375rem] mt-1">
            Estimated Gemini Live cost and the session-level drivers behind it.
          </p>
        </header>

        {error && <p className="text-error text-sm mb-5">{error}</p>}

        {items !== null && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <Stat label="Estimated Live cost" value={formatEur(totals.estimatedLiveCost)} hint="Displayed sessions" />
            <Stat label="Sessions" value={totals.sessions} />
            <Stat label="Total session time" value={formatDuration(totals.sessionSeconds)} />
            <Stat
              label="student / Elena"
              value={`${formatDuration(totals.inputSeconds)} / ${formatDuration(totals.outputSeconds)}`}
            />
            <Stat label="Model turns" value={totals.modelTurns} />
            <Stat label="Tool calls" value={totals.tools} />
          </div>
        )}

        <div className="card px-5 py-4 mb-5 text-sm text-text-secondary leading-relaxed">
          Estimates use the Gemini Live unit rates from your August 2026 billing export: €2.633 / 1M audio-input tokens, €0.658 / 1M text-input tokens, and €10.533 / 1M audio-output tokens. Google’s export has no session identifier, so this allocates the observed context, turn, and audio usage to each session rather than claiming invoice-exact attribution. Post-session analysis costs are shown as calls, not included in the Live estimate.
        </div>

        {items === null ? (
          <p className="text-text-tertiary italic text-sm">Loading session telemetry…</p>
        ) : items.length === 0 ? (
          <p className="text-text-tertiary italic text-sm">No session telemetry yet. New completed sessions will appear here.</p>
        ) : (
          <div className="flex flex-col gap-4">
            {items.map((item) => (
              <article key={item.id} className="card px-5 py-5">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-ink font-semibold">{item.user_email}</p>
                      <span className="text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-surface2 text-text-tertiary font-semibold">{item.plan}</span>
                      <span className="text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent-soft text-accent-deep font-semibold">{item.engine}</span>
                      <span className="text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-surface2 text-text-tertiary font-semibold">{item.live_error_logging.replace("_", " ")}</span>
                    </div>
                    <p className="text-xs text-text-tertiary mt-1">
                      Session #{item.session_id} · {new Date(item.started_at).toLocaleString()} · {formatDuration(item.duration_s ?? 0)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.cost_estimate_available && <span className="text-lg text-success font-semibold">{formatEur(item.estimated_live_cost_eur)}</span>}
                    {!item.counted_toward_quota && <span className="text-xs text-warning">Not counted toward quota</span>}
                    <button
                      type="button"
                      onClick={() => setCostToDelete(item)}
                      className="btn-quiet text-xs px-3 py-1.5"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2.5">
                  <Stat label="Total time" value={formatDuration(item.duration_s ?? 0)} />
                  <Stat label="Student audio" value={formatDuration(item.input_audio_seconds)} hint={`~${item.input_audio_tokens_estimate.toLocaleString()} input tokens`} />
                  <Stat label="Elena audio" value={formatDuration(item.output_audio_seconds)} />
                  <Stat label="Turns" value={`${item.student_turns} in · ${item.model_turns} out`} />
                  <Stat label="System prompt" value={`${item.system_prompt_chars.toLocaleString()} chars`} hint={`${item.memory_context_chars.toLocaleString()} memory`} />
                  <Stat label="Fresh context" value={`${item.resumed_context_chars.toLocaleString()} chars`} hint="Only after resume failure" />
                </div>

                {item.cost_estimate_available && (
                  <div className="mt-4 pt-4 border-t border-border-soft grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                    <div><span className="text-text-tertiary">Audio context: </span><span className="text-text-secondary">{formatEur(item.estimated_live_audio_input_cost_eur)} · ~{item.estimated_live_audio_input_tokens.toLocaleString()} tokens</span></div>
                    <div><span className="text-text-tertiary">Text context: </span><span className="text-text-secondary">{formatEur(item.estimated_live_text_input_cost_eur)} · ~{item.estimated_live_text_input_tokens.toLocaleString()} tokens</span></div>
                    <div><span className="text-text-tertiary">Elena audio: </span><span className="text-text-secondary">{formatEur(item.estimated_live_audio_output_cost_eur)} · ~{item.estimated_live_audio_output_tokens.toLocaleString()} tokens</span></div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border-soft grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div><span className="text-text-tertiary">Live tools: </span><ToolCalls item={item} /></div>
                  <div><span className="text-text-tertiary">Reconnects: </span><span className="text-text-secondary">{item.resumed_reconnects} resumed · {item.fresh_reconnects} fresh</span></div>
                  <div><span className="text-text-tertiary">Model calls: </span><span className="text-text-secondary">{item.post_session_model_calls} post-session · {item.pronunciation_check_calls ?? 0} pronunciation</span></div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
      {costToDelete && (
        <DeleteSessionCostModal
          item={costToDelete}
          onClose={() => setCostToDelete(null)}
          onDeleted={onCostDeleted}
        />
      )}
    </>
  );
}
